import { useState } from "react";
import { Button, Card, Input, Tag } from "antd";
import type { OutstandingQuestion } from "../types";
import { BUILDING_BLOCK_LABELS } from "../schema";
import { Check, Loader2, MessageSquare, Mic, MicOff, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface QuestionsPanelProps {
  questions: OutstandingQuestion[];
  sessionId: string;
  onQuestionsUpdated: (questions: OutstandingQuestion[]) => void;
  drafts: Record<string, string>;
  onDraftChange: (id: string, value: string) => void;
  onSubmitQuestion: (id: string, value: string) => Promise<void>;
}

export function QuestionsPanel({
  questions,
  sessionId,
  onQuestionsUpdated,
  drafts,
  onDraftChange,
  onSubmitQuestion,
}: QuestionsPanelProps) {
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [recordingForId, setRecordingForId] = useState<string | null>(null);
  const { isRecording, startRecording, stopRecording, isInitializing, isProcessingAudio } = useMicRecorder();

  const handleDismiss = async (questionId: string) => {
    setDismissingId(questionId);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/questions/${questionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        onQuestionsUpdated(data.outstandingQuestions || []);
        onDraftChange(questionId, "");
      }
    } catch (error) {
      console.error("Failed to dismiss question:", error);
    } finally {
      setDismissingId(null);
    }
  };

  const getBlockLabel = (blockKey: string | null) => {
    if (!blockKey) return "General";
    return BUILDING_BLOCK_LABELS[blockKey]?.label || blockKey;
  };

  const groupedQuestions = questions.reduce<Record<string, OutstandingQuestion[]>>(
    (acc, q) => {
      const group = q.blockKey || "general";
      if (!acc[group]) acc[group] = [];
      acc[group].push(q);
      return acc;
    },
    {},
  );

  const unansweredCount = questions.filter((q) => q.status === "pending").length;

  return (
    <div>
      <div className="px-3 pt-3 pb-2 bg-white border-b border-slate-100 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold leading-snug">Questions</div>
            <p className="text-xs text-muted-foreground">
              Answer Vo's outstanding questions to help refine and improve your roadmap.
            </p>
          </div>
          {questions.length > 0 && (
            <Tag className="text-xs shrink-0" data-testid="badge-question-count">{unansweredCount} pending</Tag>
          )}
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="px-4 pt-4">
          <div className="flex items-center justify-center py-8 border border-dashed rounded-md bg-muted/20">
            <p className="text-xs text-muted-foreground" data-testid="text-no-questions">
              No outstanding questions yet. As Vo gathers data, questions will appear here.
            </p>
          </div>
        </div>
      ) : (
      <div className="px-4 pt-3 pb-4 space-y-4">
        {Object.entries(groupedQuestions).map(([groupKey, groupQuestions]) => (
          <div key={groupKey} className="space-y-2">
            <div className="flex items-center gap-2">
              <Tag className="text-[10px] py-0">
                {getBlockLabel(groupKey === "general" ? null : groupKey)}
              </Tag>
              <span className="text-[10px] text-muted-foreground">
                {groupQuestions.length} question{groupQuestions.length > 1 ? "s" : ""}
              </span>
            </div>

            <AnimatePresence mode="popLayout">
              {groupQuestions.map((question) => {
                const isAnswered = question.status === "answered";
                const hasPendingAnswer = drafts[question.id] !== undefined && drafts[question.id] !== "";

                return (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    data-testid={`question-${question.id}`}
                  >
                    <Card
                      styles={{ body: { padding: 12 } }}
                      className={`transition-colors ${
                        isAnswered
                          ? "border-emerald-300 bg-emerald-50/50"
                          : hasPendingAnswer
                            ? "border-blue-300 bg-blue-50/50"
                            : ""
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                            <p className="text-sm" data-testid={`text-question-${question.id}`}>
                              {question.question}
                            </p>
                          </div>
                          {!isAnswered && (
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                              className="text-muted-foreground hover:text-red-500"
                              onClick={() => handleDismiss(question.id)}
                              disabled={dismissingId === question.id}
                              title="Remove question"
                              data-testid={`button-dismiss-question-${question.id}`}
                            >
                              {dismissingId === question.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <X className="h-3 w-3" />}
                            </Button>
                          )}
                        </div>

                        {isAnswered && question.userAnswer ? (
                          <div className="ml-5 pl-1">
                            <p className="text-sm text-emerald-600" data-testid={`text-answer-${question.id}`}>
                              {question.userAnswer}
                            </p>
                          </div>
                        ) : !isAnswered ? (
                          <div className="ml-5 pl-1 flex items-center gap-1">
                            <Input
                              size="small"
                              placeholder="Type your answer..."
                              value={drafts[question.id] || ""}
                              onChange={(e) => onDraftChange(question.id, e.target.value)}
                              onPressEnter={async () => {
                                const val = drafts[question.id];
                                if (!val?.trim()) return;
                                setSubmittingId(question.id);
                                await onSubmitQuestion(question.id, val);
                                setSubmittingId(null);
                              }}
                              data-testid={`input-answer-${question.id}`}
                            />
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                              className={
                                isRecording && recordingForId === question.id
                                  ? "text-red-500 hover:text-red-600"
                                  : "text-muted-foreground hover:text-foreground"
                              }
                              disabled={isInitializing || isProcessingAudio || (isRecording && recordingForId !== question.id)}
                              onClick={() => {
                                if (isRecording && recordingForId === question.id) {
                                  stopRecording();
                                  setRecordingForId(null);
                                } else {
                                  setRecordingForId(question.id);
                                  startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
                                    onDraftChange(question.id, transcript);
                                    setRecordingForId(null);
                                  });
                                }
                              }}
                              title={isRecording && recordingForId === question.id ? "Stop recording" : "Voice input"}
                            >
                              {isProcessingAudio && recordingForId === question.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : isRecording && recordingForId === question.id
                                ? <MicOff className="h-3 w-3" />
                                : <Mic className="h-3 w-3" />}
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 22, flexShrink: 0 }}
                              className="text-emerald-600 hover:text-emerald-700"
                              disabled={!drafts[question.id]?.trim() || submittingId === question.id}
                              onClick={async () => {
                                const val = drafts[question.id];
                                if (!val?.trim()) return;
                                setSubmittingId(question.id);
                                await onSubmitQuestion(question.id, val);
                                setSubmittingId(null);
                              }}
                              title="Submit answer"
                              data-testid={`button-submit-question-${question.id}`}
                            >
                              {submittingId === question.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Check className="h-3 w-3" />}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
